(() => {
  const wishlistKey = 'miyu-wishlist';

  const safeParse = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  };

  const getWishlist = () => {
    return safeParse(window.localStorage.getItem(wishlistKey) || '[]', []);
  };

  const setWishlist = (list) => {
    window.localStorage.setItem(wishlistKey, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent('wishlist-updated', { detail: { count: list.length } }));
  };

  const normalizeQuickAddButtons = () => {
    document.querySelectorAll('[data-quick-add]').forEach((btn) => {
      const labelEl = btn.querySelector('[data-quick-add-text]') || btn;
      const isDisabled = btn.disabled || btn.hasAttribute('disabled');

      btn.dataset.iconOnly = 'true';
      if (!labelEl.querySelector('svg')) {
        labelEl.textContent = isDisabled ? '-' : '+';
      }
      labelEl.classList.add('miyu-quick-add-icon');

      let srOnly = btn.querySelector('.sr-only');
      if (!srOnly) {
        srOnly = document.createElement('span');
        srOnly.className = 'sr-only';
        btn.appendChild(srOnly);
      }
      srOnly.textContent = isDisabled ? 'Sold out' : 'Add to cart';
      btn.setAttribute('aria-label', isDisabled ? 'Sold out' : 'Quick add to cart');
    });
  };

  const getProductTitleFromElement = (el) => {
    const card = el ? el.closest('[data-product-card]') : null;
    if (!card) return 'Item';
    const titleEl = card.querySelector('h3');
    if (!titleEl) return 'Item';
    const title = titleEl.textContent ? titleEl.textContent.trim().replace(/\s+/g, ' ') : '';
    return title || 'Item';
  };

  const syncWishlistButtons = () => {
    const list = getWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      const isActive = list.includes(handle);
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      btn.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Save to wishlist');
    });
  };

  window.toggleWishlist = (handle, btn) => {
    if (!handle) return;
    const list = getWishlist();
    const index = list.indexOf(handle);
    const isRemoving = index !== -1;
    const productTitle = getProductTitleFromElement(btn);

    if (isRemoving) {
      list.splice(index, 1);
    } else {
      list.unshift(handle);
    }
    setWishlist(list);
    syncWishlistButtons();

    if (window.showToast) {
      const message = isRemoving
        ? `${productTitle} removed from wishlist.`
        : `${productTitle} saved to wishlist.`;
      window.showToast(message, isRemoving ? 'info' : 'success');
    }

    if (btn) {
      btn.classList.toggle('is-active', !isRemoving);
      btn.setAttribute('aria-pressed', !isRemoving ? 'true' : 'false');
      btn.setAttribute('aria-label', !isRemoving ? 'Remove from wishlist' : 'Save to wishlist');
    }
  };

  window.updateCartCount = async () => {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) {
          el.classList.remove('hidden');
        } else {
          el.classList.add('hidden');
        }
      });
    } catch (e) {
      // Silent fail
    }
  };

  const animateArcToCart = (fromEl) => {
    const card = fromEl.closest('[data-product-card]');
    const image = card ? card.querySelector('[data-product-image]') : null;
    const cartTarget = document.querySelector('[data-cart-target]') || document.querySelector('[aria-label="Open Cart"]');
    if (!image || !cartTarget) return;

    const imageRect = image.getBoundingClientRect();
    const targetRect = cartTarget.getBoundingClientRect();

    const ghost = image.cloneNode(true);
    ghost.style.position = 'fixed';
    ghost.style.left = `${imageRect.left}px`;
    ghost.style.top = `${imageRect.top}px`;
    ghost.style.width = `${imageRect.width}px`;
    ghost.style.height = `${imageRect.height}px`;
    ghost.style.objectFit = 'cover';
    ghost.style.borderRadius = window.getComputedStyle(image).borderRadius || '24px';
    ghost.style.zIndex = '9999';
    ghost.style.pointerEvents = 'none';
    ghost.style.willChange = 'transform, opacity';
    document.body.appendChild(ghost);

    const dx = (targetRect.left + targetRect.width / 2) - (imageRect.left + imageRect.width / 2);
    const dy = (targetRect.top + targetRect.height / 2) - (imageRect.top + imageRect.height / 2);

    ghost.animate(
      [
        { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 1 },
        { transform: `translate3d(${dx}px, ${dy}px, 0) scale(0.1)`, opacity: 0.2 }
      ],
      { duration: 650, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
    );

    setTimeout(() => ghost.remove(), 700);
  };

  const handleQuickAdd = async (btn) => {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    if (btn.dataset.loading === 'true') return;
    const productTitle = getProductTitleFromElement(btn);

    const isIconOnly = btn.dataset.iconOnly === 'true';
    const labelEl = btn.querySelector('[data-quick-add-text]') || btn;
    const originalText = labelEl.textContent || '+';
    btn.dataset.loading = 'true';
    btn.disabled = true;
    labelEl.textContent = isIconOnly ? '\u2026' : 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.description || 'Could not add to cart');
      }
      labelEl.textContent = isIconOnly ? '\u2713' : 'Added';
      animateArcToCart(btn);
      if (window.updateCartCount) window.updateCartCount();
      if (window.refreshCartDrawer) window.refreshCartDrawer();
      if (window.openCartDrawer) setTimeout(() => window.openCartDrawer(), 350);
      if (window.showToast) window.showToast(`${productTitle} added to cart.`, 'success');
    } catch (e) {
      labelEl.textContent = originalText;
      if (window.showToast) window.showToast(e.message || 'Could not add to cart.', 'error');
    } finally {
      setTimeout(() => {
        labelEl.textContent = originalText;
        btn.dataset.loading = 'false';
        btn.disabled = false;
      }, 1200);
    }
  };

  const initProductGalleries = () => {
    document.querySelectorAll('[data-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryInit === 'true') return;
      gallery.dataset.galleryInit = 'true';
      const track = gallery.querySelector('[data-gallery-track]');
      const slides = track ? Array.from(track.querySelectorAll('[data-gallery-slide]')) : [];
      const dotsWrap = gallery.querySelector('[data-gallery-dots]');
      const countEl = gallery.querySelector('[data-gallery-count]');
      if (!track || slides.length === 0) return;

      if (slides.length <= 1) {
        if (dotsWrap) dotsWrap.remove();
        if (countEl) countEl.textContent = '';
        return;
      }

      if (dotsWrap && dotsWrap.childElementCount === 0) {
        slides.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'miyu-gallery-dot';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.addEventListener('click', () => {
            track.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
          });
          dotsWrap.appendChild(dot);
        });
      }

      const setActive = (index) => {
        if (dotsWrap) {
          Array.from(dotsWrap.children).forEach((dot, i) => {
            dot.classList.toggle('is-active', i === index);
          });
        }
        if (countEl) countEl.textContent = `${index + 1} / ${slides.length}`;
      };

      let raf = null;
      const onScroll = () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          const scrollLeft = track.scrollLeft;
          let bestIndex = 0;
          let minDelta = Infinity;
          slides.forEach((slide, i) => {
            const delta = Math.abs(slide.offsetLeft - scrollLeft);
            if (delta < minDelta) {
              minDelta = delta;
              bestIndex = i;
            }
          });
          setActive(bestIndex);
          raf = null;
        });
      };

      track.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();
    });
  };

  const initDecks = () => {
    document.querySelectorAll('[data-deck]').forEach((deck) => {
      if (deck.dataset.deckInit === 'true') return;
      deck.dataset.deckInit = 'true';
      const cards = Array.from(deck.querySelectorAll('[data-deck-card]'));
      if (cards.length === 0) return;
      const shell = deck.closest('.miyu-deck-wrap') || deck.parentElement;
      const prevButton = shell ? shell.querySelector('[data-deck-prev]') : null;
      const nextButton = shell ? shell.querySelector('[data-deck-next]') : null;

      let isDragging = false;
      let startX = 0;
      let currentX = 0;
      let dragCard = null;
      let moved = false;

      const layout = () => {
        cards.forEach((card, i) => {
          const offset = i * 6;
          const rotate = i * -2;
          const scale = 1 - i * 0.04;
          card.style.zIndex = `${cards.length - i}`;
          card.style.transform = `translateY(${offset}px) rotate(${rotate}deg) scale(${scale})`;
          card.style.opacity = i < 4 ? '1' : '0.2';
          card.style.transition = 'transform 0.4s ease, opacity 0.4s ease';
        });
      };

      const moveToEnd = () => {
        if (!dragCard) return;
        deck.appendChild(dragCard);
        cards.push(cards.shift());
        dragCard.style.transition = '';
        dragCard = null;
        layout();
      };

      const rotateDeck = (direction = 1) => {
        if (isDragging || cards.length < 2) return;
        dragCard = cards[0];
        const travel = direction > 0 ? 380 : -380;
        dragCard.style.transition = 'transform 0.3s ease';
        dragCard.style.transform = `translateX(${travel}px) rotate(${direction * 18}deg)`;
        setTimeout(moveToEnd, 250);
      };

      deck.addEventListener('pointerdown', (e) => {
        const topCard = cards[0];
        const targetCard = e.target.closest('[data-deck-card]');
        if (!topCard || targetCard !== topCard) return;
        isDragging = true;
        moved = false;
        dragCard = topCard;
        startX = e.clientX;
        currentX = 0;
        dragCard.setPointerCapture(e.pointerId);
        dragCard.style.transition = 'none';
      });

      deck.addEventListener('pointermove', (e) => {
        if (!isDragging || !dragCard) return;
        currentX = e.clientX - startX;
        if (Math.abs(currentX) > 3) moved = true;
        dragCard.style.transform = `translateX(${currentX}px) rotate(${currentX * 0.08}deg)`;
      });

      const endDrag = () => {
        if (!isDragging || !dragCard) return;
        isDragging = false;
        if (Math.abs(currentX) > 60) {
          const direction = currentX > 0 ? 1 : -1;
          dragCard.style.transition = 'transform 0.3s ease';
          dragCard.style.transform = `translateX(${direction * 380}px) rotate(${direction * 18}deg)`;
          setTimeout(moveToEnd, 250);
        } else {
          layout();
          dragCard = null;
        }
        currentX = 0;
      };

      deck.addEventListener('pointerup', endDrag);
      deck.addEventListener('pointercancel', endDrag);

      if (nextButton) {
        nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          rotateDeck(1);
        });
      }

      if (prevButton) {
        prevButton.addEventListener('click', (event) => {
          event.preventDefault();
          rotateDeck(-1);
        });
      }

      deck.addEventListener('click', (e) => {
        if (moved) return;
        const card = e.target.closest('[data-deck-card]');
        if (card && card.dataset.url) {
          window.location.href = card.dataset.url;
        }
      });

      layout();
    });
  };

  const initBottomDock = () => {
    if (window.__miyuDockInit) return;
    window.__miyuDockInit = true;
    const dock = document.getElementById('thumb-dock');
    if (!dock) return;
    const highlight = dock.querySelector('.miyu-dock-highlight');
    const links = Array.from(dock.querySelectorAll('.miyu-dock-link'));
    let lastY = window.scrollY;

    const moveHighlight = (link) => {
      if (!highlight || !link) return;
      const rect = link.getBoundingClientRect();
      const dockRect = dock.getBoundingClientRect();
      const x = rect.left - dockRect.left + rect.width / 2 - highlight.offsetWidth / 2;
      const y = rect.top - dockRect.top + rect.height / 2 - highlight.offsetHeight / 2;
      highlight.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      highlight.style.opacity = '0.6';
    };

    const activeLink = dock.querySelector('.is-active');
    if (activeLink) moveHighlight(activeLink);

    links.forEach((link) => {
      link.addEventListener('mouseenter', () => moveHighlight(link));
      link.addEventListener('focus', () => moveHighlight(link));
    });

    dock.addEventListener('mouseleave', () => {
      if (activeLink) moveHighlight(activeLink);
    });

    window.addEventListener('scroll', () => {
      const currentY = window.scrollY;
      const delta = currentY - lastY;

      if (currentY > 120) {
        dock.classList.add('is-compact');
      } else {
        dock.classList.remove('is-compact');
      }

      if (delta > 6 && currentY > 200) {
        dock.classList.add('is-hidden');
      } else if (delta < -6) {
        dock.classList.remove('is-hidden');
      }

      lastY = currentY;
    }, { passive: true });
  };

  const initGlobalDelegates = () => {
    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target.closest('[data-wishlist-toggle]');
      if (wishlistBtn) {
        e.preventDefault();
        e.stopPropagation();
        window.toggleWishlist(wishlistBtn.dataset.productHandle, wishlistBtn);
        return;
      }

      const quickAddBtn = e.target.closest('[data-quick-add]');
      if (quickAddBtn) {
        e.preventDefault();
        e.stopPropagation();
        handleQuickAdd(quickAddBtn);
      }
    });
  };

  const initAll = () => {
    normalizeQuickAddButtons();
    syncWishlistButtons();
    initProductGalleries();
    initDecks();
    initBottomDock();
    if (window.updateCartCount) window.updateCartCount();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initGlobalDelegates();
      initAll();
    });
  } else {
    initGlobalDelegates();
    initAll();
  }

  document.addEventListener('shopify:section:load', () => {
    initAll();
  });
})();
